<script>
  /**
   * Keys and hosts, from whichever side the caller is on.
   *
   * One page for three audiences on purpose: the columns a customer needs
   * (which of my machines are enrolled, when were they last seen) are the ones
   * a reseller needs across its book and we need across the fleet. The server
   * scopes the rows, so the only difference on screen is how many there are and
   * whether a customer column is worth showing.
   */
  import { onMount } from 'svelte';
  import Layout from '../components/Layout.svelte';
  import { enrollmentKeysApi, hostsApi, serviceEnrollmentsApi } from '../stores/api.js';
  import { _ } from '../lib/i18n.js';
  import { isCustomer } from '../stores/roles.js';
  import {
    KeyRound, Server, Plus, Loader, AlertCircle, CheckCircle, Copy, Check, Trash2, X, ShieldAlert
  } from 'lucide-svelte';

  let loading = true;
  let minting = false;
  let message = null;

  let keys = [];
  let hosts = [];
  let subscriptions = [];

  let form = { serviceEnrollmentId: '', label: '', maxHosts: '', validUntil: '' };
  let mintedKey = null;
  let copied = false;

  let revokingKey = null;
  let revokingHost = null;

  onMount(() => {
    load();
  });

  async function load() {
    loading = true;
    try {
      const [keyResponse, hostResponse, subscriptionResponse] = await Promise.all([
        enrollmentKeysApi.list(),
        hostsApi.list(),
        serviceEnrollmentsApi.getAll(),
      ]);
      keys = keyResponse.keys || [];
      hosts = hostResponse.hosts || [];
      subscriptions = subscriptionResponse.enrollments || [];
    } catch (err) {
      console.error('[Enrollment] load failed:', err);
      message = { type: 'error', text: err.message };
    } finally {
      loading = false;
    }
  }

  async function mint() {
    if (!form.serviceEnrollmentId) return;
    minting = true;
    message = null;
    try {
      const body = { serviceEnrollmentId: Number(form.serviceEnrollmentId) };
      if (form.label.trim()) body.label = form.label.trim();
      if (form.maxHosts) body.maxHosts = Number(form.maxHosts);
      if (form.validUntil) body.validUntil = form.validUntil;

      mintedKey = await enrollmentKeysApi.create(body);
      form = { serviceEnrollmentId: '', label: '', maxHosts: '', validUntil: '' };
      await load();
    } catch (err) {
      console.error('[Enrollment] mint failed:', err);
      message = { type: 'error', text: err.message };
    } finally {
      minting = false;
    }
  }

  async function copyKey(value) {
    try {
      await navigator.clipboard.writeText(value);
      copied = true;
      setTimeout(() => (copied = false), 2000);
    } catch (err) {
      console.error('[Enrollment] copy failed:', err);
    }
  }

  async function revokeKey(id) {
    try {
      await enrollmentKeysApi.revoke(id);
      message = { type: 'success', text: $_('enrollment.keyRevoked') };
      revokingKey = null;
      await load();
    } catch (err) {
      message = { type: 'error', text: err.message };
    }
  }

  async function revokeHost(id) {
    try {
      await hostsApi.revoke(id);
      message = { type: 'success', text: $_('enrollment.hostRevoked') };
      revokingHost = null;
      await load();
    } catch (err) {
      message = { type: 'error', text: err.message };
    }
  }

  function keyState(key) {
    if (key.revokedAt) return 'revoked';
    if (key.validUntil && new Date(key.validUntil) < new Date(new Date().toDateString())) return 'expired';
    return 'active';
  }

  function badgeClass(state) {
    if (state === 'active') return 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 border-emerald-300';
    if (state === 'expired') return 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 border-amber-300';
    return 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 border-red-300';
  }

  function formatDate(value) {
    if (!value) return '—';
    return new Date(value).toLocaleDateString('it-IT', { dateStyle: 'medium' });
  }

  function formatDateTime(value) {
    if (!value) return $_('enrollment.never');
    return new Date(value).toLocaleString('it-IT', { dateStyle: 'medium', timeStyle: 'short' });
  }

  function subscriptionLabel(subscription) {
    const service = subscription.variantName || subscription.variantCode || `#${subscription.id}`;
    return `${subscription.customerName} — ${service} (${formatDate(subscription.termEnd)})`;
  }
</script>

<Layout>
  <div class="space-y-6">
    <div class="flex items-center space-x-4">
      <div class="p-3 rounded-2xl shadow-lg" style="background: linear-gradient(to bottom right, #00bfff, #0090cc);">
        <KeyRound size={28} class="text-white" />
      </div>
      <div>
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white">{$_('enrollment.title')}</h1>
        <p class="text-sm text-gray-500 dark:text-gray-400">{$_('enrollment.subtitle')}</p>
      </div>
    </div>

    {#if message}
      <div class="p-4 rounded-lg flex items-center space-x-2 {message.type === 'success' ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'}">
        {#if message.type === 'success'}<CheckCircle size={20} />{:else}<AlertCircle size={20} />{/if}
        <span>{message.text}</span>
        <button class="ml-auto hover:opacity-70" onclick={() => (message = null)}>&times;</button>
      </div>
    {/if}

    {#if mintedKey}
      <div class="card p-5 border-2 border-amber-400 dark:border-amber-600 bg-amber-50 dark:bg-amber-900/20">
        <div class="flex items-start space-x-2 mb-3">
          <ShieldAlert size={20} class="text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 class="font-bold text-amber-800 dark:text-amber-200">{$_('enrollment.shownOnceTitle')}</h3>
            <p class="text-sm text-amber-700 dark:text-amber-300">{$_('enrollment.shownOnceHint')}</p>
          </div>
          <button class="ml-auto text-amber-600 hover:text-amber-800" onclick={() => (mintedKey = null)}><X size={18} /></button>
        </div>
        <div class="flex items-center gap-2">
          <code class="flex-1 px-3 py-2 bg-white dark:bg-gray-900 border border-amber-300 dark:border-amber-700 rounded font-mono text-sm break-all">{mintedKey.key}</code>
          <button class="btn btn-secondary flex items-center space-x-1" onclick={() => copyKey(mintedKey.key)}>
            {#if copied}<Check size={16} />{:else}<Copy size={16} />{/if}
            <span>{copied ? $_('ingestTokens.copied') : $_('common.copy')}</span>
          </button>
        </div>
        <p class="text-xs text-amber-700 dark:text-amber-300 mt-3">{$_('enrollment.installHint')}</p>
        <code class="block mt-1 px-3 py-2 bg-white dark:bg-gray-900 border border-amber-200 dark:border-amber-800 rounded font-mono text-xs break-all">inwd enroll --key {mintedKey.key}</code>
      </div>
    {/if}

    <!-- Mint -->
    <div class="card p-6">
      <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-1 flex items-center space-x-2">
        <Plus size={20} class="text-primary-500" />
        <span>{$_('enrollment.mintTitle')}</span>
      </h3>
      <p class="text-xs text-gray-500 dark:text-gray-400 mb-4">{$_('enrollment.mintHint')}</p>
      <div class="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div class="md:col-span-2">
          <label class="label" for="k-sub">{$_('enrollment.subscription')}</label>
          <select id="k-sub" class="input w-full" bind:value={form.serviceEnrollmentId}>
            <option value="">{$_('enrollment.subscriptionPlaceholder')}</option>
            {#each subscriptions as subscription (subscription.id)}
              <option value={subscription.id}>{subscriptionLabel(subscription)}</option>
            {/each}
          </select>
        </div>
        <div>
          <label class="label" for="k-label">{$_('enrollment.label')}</label>
          <input id="k-label" type="text" class="input w-full" placeholder={$_('enrollment.labelPlaceholder')} bind:value={form.label} />
        </div>
        <div>
          <label class="label" for="k-max">{$_('enrollment.maxHosts')}</label>
          <input id="k-max" type="number" min="1" class="input w-full" placeholder="—" bind:value={form.maxHosts} />
        </div>
        <div>
          <label class="label" for="k-until">{$_('enrollment.validUntil')}</label>
          <input id="k-until" type="date" class="input w-full" bind:value={form.validUntil} />
        </div>
      </div>
      <div class="flex justify-end mt-4">
        <button class="btn btn-primary flex items-center space-x-2" onclick={mint} disabled={minting || !form.serviceEnrollmentId}>
          {#if minting}<Loader size={18} class="animate-spin" />{:else}<Plus size={18} />{/if}
          <span>{$_('enrollment.mintButton')}</span>
        </button>
      </div>
    </div>

    {#if loading}
      <div class="card p-12 text-center">
        <Loader size={48} class="mx-auto text-primary-500 animate-spin mb-4" />
        <p class="text-gray-500 dark:text-gray-400">{$_('common.loading')}</p>
      </div>
    {:else}
      <!-- Keys -->
      <div class="card overflow-hidden">
        <div class="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center space-x-2">
          <KeyRound size={18} class="text-primary-500" />
          <h3 class="font-semibold text-gray-900 dark:text-white">{$_('enrollment.keysTitle')}</h3>
        </div>
        {#if keys.length === 0}
          <p class="p-8 text-center text-gray-500 dark:text-gray-400">{$_('enrollment.noKeys')}</p>
        {:else}
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-gray-50 dark:bg-gray-800/50">
                <tr>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('enrollment.label')}</th>
                  {#if !$isCustomer}
                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('enrollment.customer')}</th>
                  {/if}
                  <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('enrollment.hostsUsed')}</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('enrollment.validUntil')}</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('enrollment.lastUsed')}</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('enrollment.statusCol')}</th>
                  <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('common.actions')}</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
                {#each keys as key (key.id)}
                  <tr class="hover:bg-gray-50 dark:hover:bg-gray-800/50 {key.revokedAt ? 'opacity-50' : ''}">
                    <td class="px-4 py-4 text-sm text-gray-900 dark:text-white">
                      {key.label || '—'}
                      <span class="block text-xs text-gray-400 font-mono">{key.variantCode || ''}</span>
                    </td>
                    {#if !$isCustomer}
                      <td class="px-4 py-4 text-sm text-gray-600 dark:text-gray-300">{key.customerName}</td>
                    {/if}
                    <td class="px-4 py-4 text-sm text-right text-gray-600 dark:text-gray-300">
                      {key.enrolledHosts ?? 0} / {key.maxHosts}
                    </td>
                    <td class="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">{formatDate(key.validUntil)}</td>
                    <td class="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">{formatDateTime(key.lastUsedAt)}</td>
                    <td class="px-4 py-4">
                      <span class="px-2 py-0.5 text-xs font-bold border rounded {badgeClass(keyState(key))}">
                        {$_(`enrollment.${keyState(key)}`)}
                      </span>
                    </td>
                    <td class="px-4 py-4 text-right">
                      {#if !key.revokedAt}
                        {#if revokingKey === key.id}
                          <div class="flex items-center justify-end space-x-1">
                            <span class="text-xs text-gray-500 mr-2">{$_('enrollment.revokeKeyConfirm')}</span>
                            <button class="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg text-xs font-medium" onclick={() => revokeKey(key.id)}>{$_('common.confirm')}</button>
                            <button class="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg" onclick={() => (revokingKey = null)}><X size={16} /></button>
                          </div>
                        {:else}
                          <button class="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg" onclick={() => (revokingKey = key.id)} title={$_('enrollment.revokeKey')}>
                            <Trash2 size={18} />
                          </button>
                        {/if}
                      {/if}
                    </td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>
        {/if}
      </div>

      <!-- Hosts -->
      <div class="card overflow-hidden">
        <div class="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center space-x-2">
          <Server size={18} class="text-secondary-500" />
          <h3 class="font-semibold text-gray-900 dark:text-white">{$_('enrollment.hostsTitle')}</h3>
        </div>
        {#if hosts.length === 0}
          <p class="p-8 text-center text-gray-500 dark:text-gray-400">{$_('enrollment.noHosts')}</p>
        {:else}
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-gray-50 dark:bg-gray-800/50">
                <tr>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('enrollment.hostname')}</th>
                  {#if !$isCustomer}
                    <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('enrollment.customer')}</th>
                  {/if}
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('enrollment.keyLabel')}</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('enrollment.agentVersion')}</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('enrollment.lastSeen')}</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('enrollment.statusCol')}</th>
                  <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{$_('common.actions')}</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
                {#each hosts as host (host.id)}
                  <tr class="hover:bg-gray-50 dark:hover:bg-gray-800/50 {host.status === 'revoked' ? 'opacity-50' : ''}">
                    <td class="px-4 py-4 text-sm font-medium text-gray-900 dark:text-white">
                      {host.hostname}
                      <span class="block text-xs text-gray-400">{host.osRelease || ''}</span>
                    </td>
                    {#if !$isCustomer}
                      <td class="px-4 py-4 text-sm text-gray-600 dark:text-gray-300">{host.customerName}</td>
                    {/if}
                    <td class="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">{host.keyLabel || '—'}</td>
                    <td class="px-4 py-4 text-sm text-gray-500 dark:text-gray-400 font-mono">{host.agentVersion || '—'}</td>
                    <td class="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">{formatDateTime(host.lastSeen)}</td>
                    <td class="px-4 py-4">
                      <span class="px-2 py-0.5 text-xs font-bold border rounded {badgeClass(host.status === 'active' ? 'active' : 'revoked')}">
                        {host.status === 'active' ? $_('enrollment.active') : $_('enrollment.revoked')}
                      </span>
                    </td>
                    <td class="px-4 py-4 text-right">
                      {#if host.status === 'active'}
                        {#if revokingHost === host.id}
                          <div class="flex items-center justify-end space-x-1">
                            <span class="text-xs text-gray-500 mr-2">{$_('enrollment.revokeHostConfirm')}</span>
                            <button class="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg text-xs font-medium" onclick={() => revokeHost(host.id)}>{$_('common.confirm')}</button>
                            <button class="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg" onclick={() => (revokingHost = null)}><X size={16} /></button>
                          </div>
                        {:else}
                          <button class="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg" onclick={() => (revokingHost = host.id)} title={$_('enrollment.revokeHost')}>
                            <Trash2 size={18} />
                          </button>
                        {/if}
                      {/if}
                    </td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>
        {/if}
      </div>
    {/if}
  </div>
</Layout>
